/**
 * holds functions to make requests to cables_api endpoints,
 * can be given a TalkerAPI with setTalkerApi, will then relay errors to the "logError" cmd
 *
 */
class Api
{

    /**
     *
     * @param {TalkerAPI} talkerApi
     */
    setTalkerApi(talkerApi)
    {
        this._talker = talkerApi;
    }

    /**
     *
     * @param {String} sessionId
     */
    setEditorSessionId(sessionId)
    {
        this._editorSessionId = sessionId;
    }

    request(method, url, data, cbSuccess, cbError)
    {
        if (url.startsWith("/")) url = url.slice(1);

        // use global without import here, because we don't want to load all the code in sandbox_editor
        if (window.web && window.web.nav && window.web.nav.resetInfoBar) window.web.nav.resetInfoBar();
        const options = { "method": method };

        const headers = {};
        if (this._editorSessionId) headers["X-Cables-Editor-Session"] = this._editorSessionId;

        if (method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE")
        {
            headers.Accept = "application/json";
            headers["Content-Type"] = "application/json";
            if (data) options.body = JSON.stringify(data);
        }
        options.headers = headers;

        fetch("/api/" + url, options)
            .then((response) =>
            {
                let keys = response.headers.get("server-timing");
                if (keys)
                {
                    keys = keys.split(",");
                    const parts = keys[keys.length - 1].split(";");
                    if (parts.length >= 1)
                    {
                        const minTimeWarning = 350;
                        let totaltime = 0;
                        if (parts[0].trim() === "total") totaltime = parts[1].split("=")[1];
                        let time = parseFloat(totaltime);

                        const infobar = document.getElementById("info-timings");
                        if (infobar)
                        {
                            const size = ((response.headers.get("Content-Length") || 0) / 1000);

                            if (size > 750)
                            {
                                infobar.classList.remove("hidden");
                                const div = document.createElement("div");
                                div.classList.add("cute-12-tablet");

                                div.innerText = "- big request: api/" + url + ": " + Math.round(size) + "kb";
                                infobar.append(div);
                            }

                            if (time > minTimeWarning)
                            {
                                infobar.classList.remove("hidden");
                                const div = document.createElement("div");
                                div.classList.add("cute-12-tablet");

                                div.innerText = "- slow request: " + method + " /api/" + url + ": " + Math.round(time) + "ms";
                                infobar.append(div);
                            }
                        }
                        else
                        if (time > minTimeWarning)
                        {
                            this._logError({ "level": "warn", "message": "slow request: " + method + " /api/" + url + ": " + Math.round(time) + "ms" });
                        }
                    }
                }

                if (response.ok)
                {
                    // response is in 200 range, parse json, if this fails we will go
                    // into the catch block
                    if (response.headers && response.headers.get("content-type").includes("application/javascript"))
                    {
                        return response.text();
                    }
                    else
                    {
                        return response.json();
                    }
                }
                else
                {
                    // response not in 200 range, throw response as error, to be handled in catch below
                    throw response;
                }
            })
            .then((response) =>
            {
                // 200-range and valid json, call callback
                if (cbSuccess) cbSuccess(response);
            })
            .catch((e) =>
            {
                // we got here either by "not 200 range" or json parse error, so we
                // need to try the json parsing again...

                // use global without import here, because we don't want to load all the code in sandbox_editor
                if (window.web && window.web.env && window.web.env.isDev() && e.status === 502)
                {
                    this._logError({ "level": "log", "message": "BAD GATEWAY.... trying again soon....", "type": "network" });
                    setTimeout(
                        () =>
                        {
                            this.request(method, url, data, cbSuccess, cbError);
                        }, 5000
                    );
                    return;
                }

                // use global without import here, because we don't want to load all the code in sandbox_editor
                const showInInfoBar = window.web && window.web.nav && window.web.nav.showInfoBar && e.status >= 500;
                const infoBarText = "Something went wrong, please try again!";

                if (e.json)
                {
                    e.json().then((json) =>
                    {
                        if (showInInfoBar && json && json.msg && json.msg !== "UNKNOWN_ERROR")
                        {
                            window.web.nav.showInfoBar(infoBarText + "<br/>" + json.msg, "error", false);
                        }
                        // error response was not 200-range but valid json, give to callback
                        const errorMessage = " " + method + " " + (json.url || url) + " " + (json.msg || "") + " (" + (e.code || json.code) + ")";
                        this._logError({ "level": "warn", "message": errorMessage });
                        if (cbError)cbError(json);
                    }).catch((e2) =>
                    {
                        // failed to parse json of error response, give error to callback and log error to console...
                        const errorMessage = "api fetch err 1: " + method + " " + url + " " + e.message + " (" + e.code + ")";
                        this._logError({ "level": "warn", "message": errorMessage });
                        if (cbError)cbError(errorMessage);
                    });
                }
                else
                {
                    if (showInInfoBar) window.web.nav.showInfoBar(infoBarText, "error", false);
                    const errorMessage = "api fetch err 2: " + method + " " + url + " " + e.message + " (" + e.code + ")";
                    this._logError({ "level": "warn", "message": errorMessage, "type": "network" });
                    if (cbError)cbError(errorMessage);
                }
            });
    }

    get(url, cb, cbErr)
    {
        this.request("GET", url, {}, cb, cbErr);
    }

    post(url, data, cb, cbErr)
    {
        this.request("POST", url, data, cb, cbErr);
    }

    delete(url, data, cb, cbErr)
    {
        this.request("DELETE", url, data, cb, cbErr);
    }

    put(url, data, cb, cbErr)
    {
        this.request("PUT", url, data, cb, cbErr);
    }

    patch(url, data, cb, cbErr)
    {
        this.request("PATCH", url, data, cb, cbErr);
    }

    _logError(errorData)
    {
        if (!this._talker)
        {
            const errorMessage = errorData.message || "unknown error";
            switch (errorData.level)
            {
            case "error":
                console.error(errorMessage);
                break;
            case "warn":
                console.warn(errorMessage);
                break;
            case "verbose":
                console.verbose(errorMessage);
                break;
            case "info":
                console.info(errorMessage);
                break;
            default:
                console.log(errorMessage);
                break;
            }
        }
        else
        {
            this._talker.send("logError", errorData);
        }
    }
}
export default new Api();
